import React, { useState, useEffect } from 'react';
import { CreditCard } from 'lucide-react';
import Select from 'react-select';
import { CuentasBancarias } from './../../objetos/objeto_cuentas_Bancarias';
import { useFormOptions } from '../../config/constants';
import ListaCuentasBancarias from './listaCuentasBancarias';

const CuentasBancariasForm = ({ formData, setFormData , cuentas }) => {
  //lo pruimero que hacer cargar al formData las cuentas bancarias
  useEffect(() => {
    // Verificamos si 'cuentas' existe y es un array para evitar errores
    if (cuentas && Array.isArray(cuentas)) {
      setFormData(prevData => ({
        ...prevData,
        // Asignamos las cuentas recibidas. Si el array está vacío, se asigna vacío.
        cuentas: cuentas 
      }));
    }
  }, [cuentas, setFormData]);

  // Obtenemos la lista de bancos desde el hook
  const { cuentasBancariasListOptions} = useFormOptions();
    
  const [nuevaCuenta, setNuevaCuenta] = useState({
    tipoCuenta: '',
    numeroCuenta: ''
  });

  const [error, setError] = useState('');

  // Función auxiliar para obtener la opción a partir del valor
  const getOptionFromValue = (options, value) => {
    if (!value) return null;
    return options.find(option => option.value === value) || null;
  };
  
  // Manejar cambios en los inputs normales
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setNuevaCuenta({
      ...nuevaCuenta,
      [id]: value
    });
  };
  
  // Manejar cambios en los selects de react-select
  const handleSelectChange = (selectedOption, { name }) => {
    setNuevaCuenta({
      ...nuevaCuenta,
      [name]: selectedOption ? selectedOption.value : ''
    });
  };

  const validarFormulario = () => {
    if (!nuevaCuenta.tipoCuenta) {
      setError('Debes seleccionar un banco');
      return false;
    }

    if (!nuevaCuenta.numeroCuenta) {
      setError('El número de cuenta es obligatorio');
      return false;
    }
    // Validación básica para números de cuenta (solo dígitos)
    if (!/^\d+$/.test(nuevaCuenta.numeroCuenta)) {
      setError('El número de cuenta debe contener solo dígitos');
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    setError('');
    
    if (!validarFormulario()) {
      return;
    }

    // Crear una nueva instancia de CuentasBancarias
    const nuevaCuentaBancaria = new CuentasBancarias({
      tipoCuenta: nuevaCuenta.tipoCuenta,
      numeroCuenta: nuevaCuenta.numeroCuenta,
    });

    // Actualizar el formData
    setFormData({
      ...formData,
      cuentas: [...formData.cuentas, nuevaCuentaBancaria]
    });

    // Limpiar el formulario
    setNuevaCuenta({
      tipoCuenta: '',
      numeroCuenta: ''
    });
  };

  const eliminarCuenta = (index) => {
    const cuentasActualizadas = [...formData.cuentas];
    cuentasActualizadas.splice(index, 1);
    setFormData({...formData, cuentas: cuentasActualizadas});
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center text-xl font-bold text-gray-800 mb-4">
        <CreditCard className="mr-3 w-6 h-6 text-blue-500" />
        Cuentas Bancarias
      </div>
      
      <p className="text-gray-600 mb-4">
        Registra tus cuentas bancarias para recibir los pagos por tus servicios.
      </p>
      
      {formData.cuentas.length > 0 ? (
        <ListaCuentasBancarias
          cuentas={formData.cuentas}
          onDeleteCuenta={eliminarCuenta}
        />
      ) : (
        <div className="text-center p-8 border border-dashed rounded-lg text-gray-500 mb-6">
          No has añadido cuentas bancarias aún
        </div>
      )}
      
      {/* Formulario para añadir cuenta */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Añadir Cuenta Bancaria</h3>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4">
            {error}
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Banco
            </label>
            <Select 
              name="tipoCuenta"
              value={getOptionFromValue(cuentasBancariasListOptions, nuevaCuenta.tipoCuenta)}
              onChange={(option) => handleSelectChange(option, { name: 'tipoCuenta' })}
              options={cuentasBancariasListOptions}
              placeholder="Seleccione un banco"
              isClearable
              isSearchable
              classNamePrefix="react-select"
              required
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Número de Cuenta
            </label>
            <input 
              type="text" 
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              id="numeroCuenta"
              placeholder="Ej: 123456789"
              value={nuevaCuenta.numeroCuenta}
              onChange={handleInputChange}
            />
          </div>
        </div>
        
        <button 
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 w-full"
          onClick={handleSubmit}
        >
          Añadir Cuenta
        </button>
      </div>
      
      <div className="text-sm bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
        <p className="font-bold text-red-700 mb-1">⚠️ REQUISITO OBLIGATORIO</p>
        <p className="text-gray-700">
          Es <span className="font-bold underline">absolutamente imprescindible</span> que la cuenta bancaria esté a tu nombre. No se procesarán pagos a cuentas bancarias que no coincidan con la identidad del tutor registrado. Este es un requisito indispensable para recibir tus pagos.
        </p>
      </div>
    </div>
  );
};

export default CuentasBancariasForm;